import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { SceneTextItem } from './SceneTextItem';
import type { IScriptPanelProps } from '@/@types/sceneEditorV2';

function ScriptPanel({
  scenes,
  selectedSceneIndex,
  onSelectScene,
  onPlayAudio,
  onDeleteScene,
  onRegenerateAudio,
  onAddScene,
  onUpdateVoiceoverText,
  onUpdateDeliveryStyle,
  audioGeneratingSceneIds,
  audioPlayingSceneId,
  voiceoversMap,
}: IScriptPanelProps) {
  const sortedScenes = useMemo(
    () => [...scenes].sort((a, b) => a.sceneNumber - b.sceneNumber),
    [scenes]
  );

  return (
    <div className="flex h-full w-[38%] flex-shrink-0 flex-col overflow-hidden border-r border-border">
      {/* Header */}
      <div className="px-3 py-2 text-base font-medium text-foreground-muted">Script</div>

      {/* Scene list */}
      <ScrollArea className="min-h-0 flex-1">
        {sortedScenes.map((scene, index) => {
          const voiceover = voiceoversMap.get(scene._id);
          const hasAudio = voiceover?.status === 'completed' && !!voiceover.audioUrl;
          const isAudioGenerating = audioGeneratingSceneIds.includes(scene._id);

          // Only pass actual duration when audio is completed and text hasn't changed
          const isTextStale =
            hasAudio && voiceover?.voiceoverText
              ? scene.editedVoiceoverText
                ? scene.editedVoiceoverText.trim() !== voiceover.voiceoverText.trim()
                : scene.voiceoverText.trim() !== voiceover.voiceoverText.trim()
              : false;
          const actualDuration =
            hasAudio && !isTextStale && voiceover?.durationSeconds
              ? voiceover.durationSeconds
              : null;

          return (
            <SceneTextItem
              key={scene._id}
              scene={scene}
              index={index}
              isActive={index === selectedSceneIndex}
              onSelect={() => onSelectScene(index)}
              onPlayAudio={() => onPlayAudio(scene._id)}
              onDeleteScene={() => onDeleteScene(scene._id)}
              onRegenerateAudio={() => onRegenerateAudio(scene._id)}
              onUpdateVoiceoverText={text => onUpdateVoiceoverText(scene._id, text)}
              onUpdateDeliveryStyle={prompt => onUpdateDeliveryStyle(scene._id, prompt)}
              isAudioGenerating={isAudioGenerating}
              isAudioPlaying={audioPlayingSceneId === scene._id}
              hasAudio={hasAudio}
              generatedVoiceoverText={voiceover?.voiceoverText ?? null}
              actualDurationSeconds={actualDuration}
            />
          );
        })}
      </ScrollArea>

      {/* Add scene button */}
      <div className="border-t border-border px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-1.5 text-foreground-muted"
          onClick={onAddScene}
        >
          <Plus className="size-4" />
          Add scene
        </Button>
      </div>
    </div>
  );
}

export { ScriptPanel };
